/**
 * Centralized file type constants for attachment uploads
 *
 * @overview
 * These constants match the API types defined in types/api.d.ts for the upload endpoint.
 * Any changes to supported file types should be made here and will automatically
 * propagate to all components that use file uploads.
 *
 * @supported_types
 * - PDF documents: application/pdf
 * - Word documents: .doc, .docx
 * - Excel files: .xls, .xlsx, .csv
 * - Images: JPEG, PNG, WebP, HEIC
 *
 * @constraints
 * - Maximum file size: 25 MB per file (all supported types)
 * - Maximum file count: 3 files per upload
 */

export const ALLOWED_FILE_TYPES = [
  // PDF types
  'application/pdf',
  // Word types
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // Excel types
  'application/vnd.ms-excel',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // Image types
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  // Video types
  'video/quicktime',
  'video/mp4',
];

export const FILE_CONSTRAINTS = {
  MAX_IMAGE_SIZE_MB: 25,
  MAX_VIDEO_SIZE_MB: 25,
  MAX_OTHER_SIZE_MB: 25,
  MAX_FILE_COUNT: 3,
} as const;

export const IMAGE_MAX_SIZE = FILE_CONSTRAINTS.MAX_IMAGE_SIZE_MB * 1024 * 1024;
export const VIDEO_MAX_SIZE = FILE_CONSTRAINTS.MAX_VIDEO_SIZE_MB * 1024 * 1024;
export const OTHER_MAX_SIZE = FILE_CONSTRAINTS.MAX_OTHER_SIZE_MB * 1024 * 1024;

/**
 * Returns the maximum file size in bytes based on the file's MIME type.
 * Images: 25 MB, Videos: 25 MB, Other files: 25 MB
 */
export const getMaxFileSize = (mimeType: string): number => {
  if (mimeType.startsWith('image/')) {
    return IMAGE_MAX_SIZE;
  }

  if (mimeType.startsWith('video/')) {
    return VIDEO_MAX_SIZE;
  }

  return OTHER_MAX_SIZE;
};

/**
 * Returns the maximum file size in MB based on the file's MIME type.
 * Images: 25 MB, Videos: 25 MB, Other files: 25 MB
 */
export const getMaxFileSizeMB = (mimeType: string): number => {
  if (mimeType.startsWith('image/')) {
    return FILE_CONSTRAINTS.MAX_IMAGE_SIZE_MB;
  }

  if (mimeType.startsWith('video/')) {
    return FILE_CONSTRAINTS.MAX_VIDEO_SIZE_MB;
  }

  return FILE_CONSTRAINTS.MAX_OTHER_SIZE_MB;
};

export const SUPPORTED_FILE_TYPES_DISPLAY =
  'Supported: Images, PDF, Word, Excel, CSV, MP4, MOV • Max 25 MB per file';

export interface FileItem {
  name: string;
  size: number;
  mimeType: string;
  uri: string;
  file: any;
}
